import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ToggleButton,
  Typography,
} from "@mui/material";
import bkstApi from "../../api";
import moment from "moment";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import ButtonWithConfirmation from "../../common/ButtonWithConfirmation";
import RadioCardGroup from "../../common/component/RadioCardGroup";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { deliveryTimeSlots, pickupTimeSlots } from "../../common/constants";
import { getStartTime } from "../../common/util";

const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function actionOptions(showDelivery) {
  return [
    {
      value: "block",
      title: "Block a Date",
      description: showDelivery
        ? "Close pickup and/or delivery for a specific date"
        : "Close pickup for a specific date",
    },
    {
      value: "specialHours",
      title: "Set Special Hours",
      description: "Limit available timeslots for a specific date",
    },
  ];
}

const ACTION_TITLE = {
  block: "Block a Date",
  specialHours: "Set Special Hours",
};

function weekdayKey(dateStr) {
  return WEEKDAY_KEYS[moment(dateStr, "YYYY-MM-DD").isoWeekday() - 1];
}

function weeklySlots(storeLocation, dateStr, mode) {
  if (!dateStr) return [];
  return storeLocation.fulfillment?.[mode]?.timeslots?.[weekdayKey(dateStr)] || [];
}

/**
 * Fixed lists: hourly `pickupTimeSlots` for pickup, 2-hour `deliveryTimeSlots` for delivery.
 * Weekly labels for that weekday and previously saved labels outside the list are added so
 * editing never drops them.
 */
function slotOptionsFor(storeLocation, dateStr, channel, saved = []) {
  const base = channel === "pickup" ? pickupTimeSlots : deliveryTimeSlots;
  const weekly = weeklySlots(storeLocation, dateStr, channel);
  const extras = [...weekly, ...saved].filter((s) => !base.includes(s));
  if (extras.length === 0) return base;
  return [...new Set([...base, ...extras])].sort((a, b) => getStartTime(a) - getStartTime(b));
}

/**
 * Preselected slots for a new override: the store's regular weekly labels for that weekday,
 * matched by exact string.
 */
function defaultSlotsFor(storeLocation, dateStr, channel) {
  return [...weeklySlots(storeLocation, dateStr, channel)];
}

/** Stored slots for a channel. Missing delivery inherits pickup. undefined = no override. */
function slotsFromEntry(entry, channel) {
  if (Array.isArray(entry)) return entry;
  if (entry && Array.isArray(entry[channel])) return entry[channel];
  if (channel === "delivery" && entry && Array.isArray(entry.pickup)) return entry.pickup;
  return undefined;
}

function emptyChannelSlots() {
  return { pickup: [], delivery: [] };
}

function channelStatus(label, slots) {
  const count = Array.isArray(slots) ? slots.length : 0;
  if (count === 0) return `${label} closed`;
  return `${label} ${count} ${count === 1 ? "slot" : "slots"}`;
}

function ChannelSummary({ label, slots }) {
  const closed = !Array.isArray(slots) || slots.length === 0;
  return (
    <Box mb={1}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      {closed ? (
        <Typography variant="body2" color="error">
          Closed
        </Typography>
      ) : (
        <Box>
          {slots.map((t) => (
            <Chip key={`${label}-${t}`} label={t} sx={{ mr: 0.5, mb: 0.5 }} />
          ))}
        </Box>
      )}
    </Box>
  );
}

function SlotColumn({
  title,
  master,
  selected,
  weeklyEmpty,
  weekdayLabel,
  onToggle,
  onSelectAll,
  onClear,
}) {
  return (
    <Box sx={{ minWidth: 0, width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Button size="small" onClick={onSelectAll} disabled={master.length === 0}>
            Select all
          </Button>
          <Button size="small" onClick={onClear}>
            Clear
          </Button>
        </Box>
      </Box>

      {weeklyEmpty && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Your weekly schedule has no {title.toLowerCase()} hours on {weekdayLabel}.
        </Alert>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1 }}>
        {master.map((slot) => {
          const on = selected.includes(slot);
          return (
            <ToggleButton
              key={`${title}-${slot}`}
              value={slot}
              selected={on}
              onClick={() => onToggle(slot, !on)}
              color="primary"
              sx={{
                py: 1.25,
                px: 1.5,
                width: "100%",
                justifyContent: "flex-start",
                gap: 1,
                textTransform: "none",
                fontWeight: 500,
                borderRadius: 1,
                typography: "body2",
              }}
            >
              <Checkbox
                checked={on}
                size="small"
                color="primary"
                tabIndex={-1}
                sx={{ p: 0, pointerEvents: "none" }}
              />
              {slot}
            </ToggleButton>
          );
        })}
      </Box>
    </Box>
  );
}

export default function StoreClosureV2(props) {
  const { storeLocation, onStoreLocationsUpdated, showDelivery = true } = props;

  const { block, specialHours } = useMemo(() => {
    const dateRules = storeLocation.rules?.date || {};
    return {
      block: dateRules.block || [],
      specialHours: { ...(dateRules.specialHours || {}) },
    };
  }, [storeLocation]);

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [dialogMode, setDialogMode] = useState("create");
  const [action, setAction] = useState("");
  const [formDate, setFormDate] = useState("");
  const [blockPickup, setBlockPickup] = useState(true);
  const [blockDelivery, setBlockDelivery] = useState(true);
  const [channelSlots, setChannelSlots] = useState(emptyChannelSlots);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  const mergedDateKeys = useMemo(() => {
    return [...new Set([...block, ...Object.keys(specialHours)])];
  }, [block, specialHours]);

  const displayDates = useMemo(
    () => mergedDateKeys.filter((o) => o >= moment().format("YYYY-MM-DD")).sort(),
    [mergedDateKeys]
  );

  const pickupMaster = useMemo(
    () =>
      slotOptionsFor(
        storeLocation,
        formDate,
        "pickup",
        slotsFromEntry(specialHours[formDate], "pickup")
      ),
    [storeLocation, formDate, specialHours]
  );
  const deliveryMaster = useMemo(
    () =>
      slotOptionsFor(
        storeLocation,
        formDate,
        "delivery",
        slotsFromEntry(specialHours[formDate], "delivery")
      ),
    [storeLocation, formDate, specialHours]
  );

  const initSlotsForDate = (dateStr) => ({
    pickup: defaultSlotsFor(storeLocation, dateStr, "pickup"),
    delivery: defaultSlotsFor(storeLocation, dateStr, "delivery"),
  });

  const resetDialog = () => {
    setStep(1);
    setAction("");
    setFormDate("");
    setBlockPickup(true);
    setBlockDelivery(true);
    setChannelSlots(emptyChannelSlots());
    setDialogMode("create");
    setProcessing(false);
    setError("");
  };

  const handleClose = () => {
    setOpen(false);
    resetDialog();
  };

  const openCreate = () => {
    resetDialog();
    setOpen(true);
  };

  const openEdit = (dateStr) => {
    setProcessing(false);
    setError("");
    setDialogMode("edit");
    setStep(1);
    setFormDate(dateStr);

    const day = specialHours[dateStr];
    const blockOnly = block.includes(dateStr) && day === undefined;
    const pickupSlots = blockOnly ? [] : slotsFromEntry(day, "pickup");
    const deliverySlots = blockOnly ? [] : slotsFromEntry(day, "delivery");
    const pickupClosed = Array.isArray(pickupSlots) && pickupSlots.length === 0;
    const deliveryClosed = Array.isArray(deliverySlots) && deliverySlots.length === 0;
    const isBlockAction =
      blockOnly || (pickupClosed && (deliveryClosed || !showDelivery));

    if (isBlockAction) {
      setAction("block");
      setBlockPickup(blockOnly || pickupClosed || pickupSlots === undefined);
      setBlockDelivery(blockOnly || deliveryClosed || deliverySlots === undefined);
      setChannelSlots(initSlotsForDate(dateStr));
    } else {
      setAction("specialHours");
      setBlockPickup(true);
      setBlockDelivery(true);
      const pickupOptions = slotOptionsFor(storeLocation, dateStr, "pickup", pickupSlots);
      const deliveryOptions = slotOptionsFor(storeLocation, dateStr, "delivery", deliverySlots);
      setChannelSlots({
        pickup: Array.isArray(pickupSlots)
          ? pickupOptions.filter((s) => pickupSlots.includes(s))
          : defaultSlotsFor(storeLocation, dateStr, "pickup"),
        delivery: Array.isArray(deliverySlots)
          ? deliveryOptions.filter((s) => deliverySlots.includes(s))
          : defaultSlotsFor(storeLocation, dateStr, "delivery"),
      });
    }

    setOpen(true);
  };

  const handleActionChange = (value) => {
    setAction(value);
    if (dialogMode === "create") {
      setBlockPickup(true);
      setBlockDelivery(true);
      if (formDate) setChannelSlots(initSlotsForDate(formDate));
      else setChannelSlots(emptyChannelSlots());
    }
  };

  const handleFormDateChange = (d) => {
    setFormDate(d);
    if (dialogMode === "create" && d) {
      setChannelSlots(initSlotsForDate(d));
      setBlockPickup(true);
      setBlockDelivery(true);
    }
  };

  const toggleSlot = (channel, slot, checked) => {
    setChannelSlots((prev) => {
      const current = prev[channel];
      const slots = checked
        ? current.includes(slot)
          ? current
          : [...current, slot]
        : current.filter((s) => s !== slot);
      return { ...prev, [channel]: slots };
    });
  };

  const handleSubmit = () => {
    if (!formDate || !action) return;

    const payload = { action: "ADD", date: formDate };

    if (!showDelivery) {
      payload.pickup =
        action === "block" ? [] : pickupMaster.filter((s) => channelSlots.pickup.includes(s));
    } else if (action === "block") {
      if (!blockPickup && !blockDelivery) return;
      payload.pickup = blockPickup ? [] : weeklySlots(storeLocation, formDate, "pickup");
      payload.delivery = blockDelivery ? [] : weeklySlots(storeLocation, formDate, "delivery");
    } else {
      payload.pickup = pickupMaster.filter((s) => channelSlots.pickup.includes(s));
      payload.delivery = deliveryMaster.filter((s) => channelSlots.delivery.includes(s));
    }

    const hadBlock = block.includes(formDate);

    setProcessing(true);
    setError("");
    // Not atomic: if the legacy block DELETE fails, the date keeps both entries (both still close it).
    bkstApi
      .put(`/my/store-location/v2/${storeLocation.id}`, payload)
      .then(() => {
        if (hadBlock) {
          return bkstApi.put(`/my/store-location/${storeLocation.id}`, {
            action: "DELETE",
            rules: { date: { block: formDate } },
          });
        }
      })
      .then(() => onStoreLocationsUpdated?.())
      .then(() => handleClose())
      .catch((err) => handleRequestError(err, "Failed to save. Please try again."));
  };

  const handleRequestError = (err, fallback) => {
    console.log(err);
    setProcessing(false);
    setError(err?.response?.data?.message || fallback);
    onStoreLocationsUpdated?.();
  };

  const deleteSpecialDateOverride = (dateStr) => {
    const hasSpecialHours = Object.prototype.hasOwnProperty.call(specialHours, dateStr);
    const hasBlock = block.includes(dateStr);

    const run = () => {
      if (hasSpecialHours) {
        return bkstApi.put(`/my/store-location/v2/${storeLocation.id}`, {
          action: "DELETE",
          date: dateStr,
        });
      }
      return Promise.resolve();
    };

    return run()
      .then(() => {
        if (hasBlock) {
          return bkstApi.put(`/my/store-location/${storeLocation.id}`, {
            action: "DELETE",
            rules: { date: { block: dateStr } },
          });
        }
      })
      .then(() => onStoreLocationsUpdated?.());
  };

  // Pickup-only locations have nothing to configure after picking the date for a block.
  const lastStep =
    !showDelivery && action === "block" ? (dialogMode === "edit" ? 1 : 2) : 3;

  const goNext = () => {
    if (step === 1 && action) {
      setStep(dialogMode === "edit" ? 3 : 2);
    } else if (step === 2 && formDate) {
      setStep(3);
    }
  };

  const goBack = () => {
    if (step === 3 && dialogMode === "edit") {
      setStep(1);
    } else if (step > 1) {
      setStep((s) => s - 1);
    }
  };

  const nextDisabled =
    (step === 1 && !action) ||
    (step === 2 && !formDate) ||
    (step === 3 && action === "block" && !blockPickup && !blockDelivery);

  const saveDisabled =
    !formDate ||
    !action ||
    (action === "block" && !blockPickup && !blockDelivery) ||
    (action === "specialHours" && pickupMaster.length === 0 && deliveryMaster.length === 0);

  const formattedDate = formDate
    ? moment(formDate, "YYYY-MM-DD").format("MMM D, YYYY")
    : "";

  const dialogTitle = (() => {
    if (dialogMode === "edit" && formattedDate) {
      if (step === 1) return `Edit — ${formattedDate}`;
      return `${ACTION_TITLE[action] || "Hours"} — ${formattedDate}`;
    }
    if (step === 1) return "Blackout & Special Hours";
    if (step === 2) return ACTION_TITLE[action] || "Select a Date";
    return `${ACTION_TITLE[action] || "Hours"} — ${formattedDate}`;
  })();

  const weekdayLabel = formDate ? moment(formDate, "YYYY-MM-DD").format("dddd") : "";

  const deleteButton =
    dialogMode === "edit" && formDate ? (
      <Box sx={{ flex: "1 1 50%", minWidth: 0 }}>
        <ButtonWithConfirmation
          fullWidth
          disabled={processing}
          title="Remove override?"
          copy={`Remove the override for ${moment(formDate, "YYYY-MM-DD").format("MMM D, YYYY")}? Customers will use your regular weekly hours for that day.`}
          onConfirm={() => {
            setProcessing(true);
            setError("");
            deleteSpecialDateOverride(formDate)
              .then(() => handleClose())
              .catch((err) => handleRequestError(err, "Failed to delete. Please try again."));
          }}
        >
          <Button fullWidth color="error" variant="outlined" disabled={processing}>
            Delete
          </Button>
        </ButtonWithConfirmation>
      </Box>
    ) : null;

  const toggleChannelTile = (channel, checked) => {
    if (channel === "pickup") {
      if (!checked && !blockDelivery) return;
      setBlockPickup(checked);
    } else {
      if (!checked && !blockPickup) return;
      setBlockDelivery(checked);
    }
  };

  return (
    <Box>
      <Box mt={displayDates.length > 0 ? 1.5 : 0}>
        {displayDates.map((d) => {
          const day = specialHours[d];
          const blockOnly = block.includes(d) && day === undefined;
          const pickupSlots = blockOnly ? [] : slotsFromEntry(day, "pickup") || [];
          const deliverySlots = blockOnly ? [] : slotsFromEntry(day, "delivery") || [];

          return (
            <Box
              key={d}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
                mb: 1.5,
              }}
            >
              <Accordion
                elevation={0}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreOutlinedIcon />}
                  sx={{ minHeight: 56, borderRadius: 1 }}
                >
                  <Box
                    sx={{
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      color="text.primary"
                      sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                    >
                      {moment(d, "YYYY-MM-DD").format("ddd, MMM D, YYYY").toUpperCase()}
                    </Typography>
                    <Typography variant="overline" color="text.secondary">
                      {showDelivery
                        ? `${channelStatus("pickup", pickupSlots)} • ${channelStatus(
                            "delivery",
                            deliverySlots
                          )}`
                        : channelStatus("pickup", pickupSlots)}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <ChannelSummary label="Pickup" slots={pickupSlots} />
                  {showDelivery && <ChannelSummary label="Delivery" slots={deliverySlots} />}
                </AccordionDetails>
              </Accordion>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                  pr: 0.5,
                  minHeight: 56,
                }}
              >
                <IconButton
                  size="small"
                  aria-label={`Edit special hours for ${d}`}
                  onClick={() => openEdit(d)}
                  sx={{ padding: 0.5 }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          );
        })}
      </Box>
      <Box mt={2}>
        <Button
          fullWidth
          color="primary"
          variant="contained"
          disableElevation
          startIcon={<AddOutlinedIcon />}
          onClick={openCreate}
        >
          ADD NEW DATE
        </Button>
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth={step === 3 && action === "specialHours" && showDelivery ? "md" : "sm"}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            {step > 1 ? (
              <IconButton aria-label="Back" onClick={goBack} size="small" sx={{ ml: -0.5 }}>
                <ArrowBackIcon />
              </IconButton>
            ) : null}
            {dialogTitle}
          </Box>
          <IconButton aria-label="Close" onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box mt={2}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}
            {step === 1 && (
              <Box>
                <Typography variant="h4" gutterBottom>
                  What would you like to do?
                </Typography>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                  {dialogMode === "edit" && formattedDate
                    ? `Editing ${formattedDate}. Choose whether to block this date or set special hours.`
                    : "Choose whether to block a date entirely or set special hours."}
                </Typography>
                <RadioCardGroup
                  direction="row"
                  name="action"
                  value={action}
                  onChange={(e) => handleActionChange(e.target.value)}
                  options={actionOptions(showDelivery)}
                />
              </Box>
            )}

            {step === 2 && (
              <Box>
                <Typography variant="h4" gutterBottom>
                  Select a Date
                </Typography>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                  {action === "block"
                    ? "Pick the date you want to block."
                    : "Pick the date for special hours."}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <StaticDatePicker
                    disablePast
                    disabled={dialogMode === "edit"}
                    format="MMM d, y"
                    value={formDate ? moment(formDate, "YYYY-MM-DD").toDate() : null}
                    onChange={(v) =>
                      handleFormDateChange((v && moment(v).format("YYYY-MM-DD")) || "")
                    }
                    shouldDisableDate={(d) => {
                      const key = moment(d).format("YYYY-MM-DD");
                      if (dialogMode === "edit" && key === formDate) return false;
                      return mergedDateKeys.includes(key);
                    }}
                    slotProps={{
                      actionBar: { actions: [] },
                      toolbar: { hidden: true },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            )}

            {step === 3 && action === "block" && (
              <Box>
                <Typography variant="h4" gutterBottom>
                  {moment(formDate, "YYYY-MM-DD").format("ddd MMM D, YYYY")}
                </Typography>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                  Choose which channels to block. At least one is required.
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 1.5,
                  }}
                >
                  {[
                    { key: "pickup", label: "Pickup", checked: blockPickup },
                    { key: "delivery", label: "Delivery", checked: blockDelivery },
                  ].map((opt) => (
                    <FormControlLabel
                      key={opt.key}
                      control={
                        <Checkbox
                          checked={opt.checked}
                          onChange={(e) => toggleChannelTile(opt.key, e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="subtitle2" fontWeight={600}>
                          {opt.label}
                        </Typography>
                      }
                      sx={(theme) => ({
                        m: 0,
                        width: "100%",
                        boxSizing: "border-box",
                        border: "2px solid",
                        borderColor: opt.checked ? "primary.main" : theme.palette.divider,
                        borderRadius: 2,
                        px: 1.75,
                        py: 1.5,
                        display: "flex",
                        flexDirection: "row-reverse",
                        justifyContent: "space-between",
                        alignItems: "center",
                        "& .MuiFormControlLabel-label": { margin: 0 },
                      })}
                    />
                  ))}
                </Box>
                {(!blockPickup || !blockDelivery) && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                    {!blockPickup && !blockDelivery
                      ? null
                      : !blockPickup
                        ? "Pickup keeps this weekday's regular hours."
                        : "Delivery keeps this weekday's regular hours."}
                  </Typography>
                )}
              </Box>
            )}

            {step === 3 && action === "specialHours" && (
              <Box>
                <Typography variant="h4" gutterBottom>
                  {moment(formDate, "YYYY-MM-DD").format("ddd MMM D, YYYY")}
                </Typography>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                  Select the timeslots that should remain available.
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: showDelivery ? "1fr 1fr" : "1fr" },
                    gap: 2,
                    alignItems: "start",
                  }}
                >
                  <SlotColumn
                    title="Pickup"
                    master={pickupMaster}
                    selected={channelSlots.pickup}
                    weeklyEmpty={weeklySlots(storeLocation, formDate, "pickup").length === 0}
                    weekdayLabel={weekdayLabel}
                    onToggle={(slot, checked) => toggleSlot("pickup", slot, checked)}
                    onSelectAll={() =>
                      setChannelSlots((prev) => ({ ...prev, pickup: [...pickupMaster] }))
                    }
                    onClear={() => setChannelSlots((prev) => ({ ...prev, pickup: [] }))}
                  />

                  {showDelivery && (
                    <SlotColumn
                      title="Delivery"
                      master={deliveryMaster}
                      selected={channelSlots.delivery}
                      weeklyEmpty={weeklySlots(storeLocation, formDate, "delivery").length === 0}
                      weekdayLabel={weekdayLabel}
                      onToggle={(slot, checked) => toggleSlot("delivery", slot, checked)}
                      onSelectAll={() =>
                        setChannelSlots((prev) => ({ ...prev, delivery: [...deliveryMaster] }))
                      }
                      onClear={() => setChannelSlots((prev) => ({ ...prev, delivery: [] }))}
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 2, display: "block" }}>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              gap: 1.5,
              alignItems: "stretch",
            }}
          >
            {deleteButton}
            {step < lastStep ? (
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={goNext}
                disabled={nextDisabled}
                sx={{
                  flex: deleteButton ? "1 1 50%" : "1 1 100%",
                  minWidth: 0,
                }}
              >
                Next
              </Button>
            ) : (
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={saveDisabled || processing}
                sx={{
                  flex: deleteButton ? "1 1 50%" : "1 1 100%",
                  minWidth: 0,
                }}
              >
                Save
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
